import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiContext } from '../../context/ApiContext/ApiContext';
import { SurveySubmission } from '../../api/survey/survey.models';
import { InvalidationTag, LoaderType, useAppQuery } from './utils';

export const useGetCurrentSurvey = (companyId?: string) => {
  const { surveyApi } = useContext(ApiContext);

  return useAppQuery(
    [InvalidationTag.CurrentSurvey, companyId],
    async () => {
      if (!companyId) {
        return { item: null };
      }
      return surveyApi.getCurrentSurvey({ companyId });
    },
    {
      enabled: !!companyId,
    },
    LoaderType.FullPage
  );
};

export const useGetPendingSurveys = (companyId?: string) => {
  const { surveyApi } = useContext(ApiContext);

  return useAppQuery(
    [InvalidationTag.PendingSurveys, companyId],
    async () => {
      if (!companyId) {
        return { item: null };
      }
      return surveyApi.getPendingSurveys({ companyId });
    },
    {
      enabled: !!companyId,
    },
    LoaderType.FullPage
  );
};

export const useGetSurveyDetail = (companyId?: string, surveyId?: string) => {
  const { surveyApi } = useContext(ApiContext);

  return useQuery({
    queryKey: [InvalidationTag.SurveyDetail, companyId, surveyId],
    queryFn: () => {
      if (!companyId || !surveyId) {
        return { item: null };
      }
      return surveyApi.getSurveyDetail({ companyId, surveyId });
    },
    enabled: !!companyId && !!surveyId,
  });
};

export const useSubmitSurveyAnswers = () => {
  const { surveyApi } = useContext(ApiContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (submission: SurveySubmission) => {
      return surveyApi.submitSurveyAnswers(submission);
    },
    onSuccess: (_, variables) => {
      // Invalidate the current survey query to force a refetch
      queryClient.invalidateQueries({ queryKey: [InvalidationTag.CurrentSurvey, variables.companyId] });
      // Invalidate the pending surveys query
      queryClient.invalidateQueries({ queryKey: [InvalidationTag.PendingSurveys, variables.companyId] });
      // Invalidate the survey detail query
      queryClient.invalidateQueries({ 
        queryKey: [InvalidationTag.SurveyDetail, variables.companyId, variables.surveyId] 
      });
      // Invalidate the completed surveys query
      queryClient.invalidateQueries({ queryKey: [InvalidationTag.CompletedSurveys, variables.companyId] });
      // Invalidate the user KPI scores query to refresh the KPI gauge charts
      queryClient.invalidateQueries({ queryKey: [InvalidationTag.UserKpiScores, variables.companyId] });
    },
  });
};

export const useGetCompletedSurveys = (companyId?: string) => {
  const { surveyApi } = useContext(ApiContext);

  return useAppQuery(
    [InvalidationTag.CompletedSurveys, companyId],
    async () => {
      if (!companyId) {
        return { items: [] };
      }
      return surveyApi.getCompletedSurveys({ companyId });
    },
    {
      enabled: !!companyId,
    },
    LoaderType.FullPage
  );
};

export const useGetUserKpiScores = (companyId?: string, enabled: boolean = true) => {
  const { surveyApi } = useContext(ApiContext);

  return useAppQuery(
    [InvalidationTag.UserKpiScores, companyId],
    async () => {
      if (!companyId) {
        return { item: { kpiScores: [], cycleId: '', completedSurveys: 0, totalSurveys: 0, completionPercentage: 0 } };
      }
      return surveyApi.getUserKpiScores({ companyId });
    },
    {
      enabled: !!companyId && enabled,
    },
    LoaderType.FullPage
  );
};
